import { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { Sidebar, MobileNav, Icon, Logo, type MenuItem } from '@chikara/ui';
import { LogoutConfirmationModal } from '@/features/auth/components/LogoutConfirmationModal';
import { SessionTimeoutManager } from '@/features/auth/components/SessionTimeoutManager';
import { isBasic } from '@/shared/config/permissions';
import styles from './DashboardLayout.module.css';

export default function DashboardLayout() {
  const { logout, userData, activeCompany } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const formatLastAccess = (date: Date = new Date()): string => {
    const months = [
      'ene', 'feb', 'mar', 'abr', 'may', 'jun',
      'jul', 'ago', 'sep', 'oct', 'nov', 'dic'
    ];
    
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `Último acceso a cuenta: ${day} de ${month} de ${year} a las ${hours}:${minutes} hrs.`;
  };

  const basic = isBasic(userData.role);

  const mainMenuItems: MenuItem[] = userData.role ? [
    {
      icon: <Icon name="bank" />,
      label: 'Actividad',
      href: '/dashboard',
      isActive: location.pathname === '/dashboard',
    },
    {
      icon: <Icon name="credit-card" />,
      label: 'Tarjeta',
      href: '/card',
      isActive: location.pathname === '/card' || location.pathname.startsWith('/card/')
    },
    ...(!basic ? [
      {
        icon: <Icon name="users" />,
        label: 'Equipo',
        href: '/team',
        isActive: location.pathname === '/team' || location.pathname.startsWith('/team/')
      },
      {
        icon: <Icon name="currency" />,      
        label: 'Crédito',
        href: '/credit',
        isActive: location.pathname === '/credit' || location.pathname.startsWith('/credit/'),
      }
    ] : [])
  ] : [];

  // Items del footer (ordenados: Centro de Ayuda primero, luego Cerrar sesión)
  const footerMenuItems: MenuItem[] = [
    {
      icon: <Icon name="support" />,
      label: 'Centro de Ayuda',
      href: 'https://ayuda.delt.ai/hc/es-419/requests/new',
      target: '_blank',
      rel: 'noopener noreferrer',
      style: { marginBottom: 0 },
    },
    {
      icon: <Icon name="close" />,
      label: 'Cerrar sesión',
      onClick: (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        setIsLogoutModalOpen(true);
        if (isMobile) {
          setIsSidebarOpen(false);
        }
      },
      variant: 'danger',
    },
  ];

  // Combinar todos los items en el orden correcto
  const menuItems: MenuItem[] = [...mainMenuItems, ...footerMenuItems];

  const handleNavItemClick = (e: React.MouseEvent<HTMLElement>, href?: string) => {
    if (href?.startsWith('http')) {
      return;
    }
    e.preventDefault();
    if (href) {
      navigate(href);
    }
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  const menuItemsWithHandlers = menuItems.map((item) => {
    if (item.onClick) {
      return item;
    }
    return {
      ...item,
      onClick: (e: React.MouseEvent<HTMLElement>) => handleNavItemClick(e, item.href),
    };
  });

  const logoElement = (
    <img src="/logo_delta.svg" alt="delt.ai" width={32} height={32} />
  );

  return (
    <div className={styles.layout}>
      <MobileNav
        logo={logoElement}
        onMenuOpen={() => setIsSidebarOpen(true)}
      />
      <Sidebar
        logo={isMobile ? <Logo variant="full" size="md" /> : <Logo variant="symbol" size="md" />}
        logoHref="/dashboard"
        menuItems={menuItemsWithHandlers}
        footerItemLabels={['Centro de Ayuda', 'Cerrar sesión']}
        footerText={formatLastAccess()}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />
      <div className={styles.main}>
        <main className={styles.content} key={activeCompany?.customer_uuid ?? 'single-company'}>
          <Outlet />
        </main>
      </div>
      <LogoutConfirmationModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={() => {
          setIsLogoutModalOpen(false);
          logout();
        }}
      />
      <SessionTimeoutManager />
    </div>
  );
}

export { DashboardLayout };
